import {
    Firestore,
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    CollectionReference,
    DocumentData,
    getCountFromServer
} from "firebase/firestore";

import { fs } from "../firebase.ts";
import { Feedback, Response, Responses } from "../models/Feedback.ts";

export class Feedbacks {
    private fs: Firestore;
    private collection: CollectionReference<DocumentData>;

    constructor(firestore: Firestore | null = null) {
        this.fs = firestore || fs;
        this.collection = collection(this.fs, "feedbacks");
    }

    async count(): Promise<number> {
        const snapshot = await getCountFromServer(this.collection);
        return snapshot.data().count;
    }

    // CREATE - Add new feedback
    async createFeedback(
        feedback: Omit<Feedback, "createdAt">
    ): Promise<Response> {
        try {
            const feedbackData = {
                createdAt: new Date(),
                ...feedback
            };

            const docRef = await addDoc(this.collection, feedbackData);
            const newFeedback = {
                id: docRef.id,
                ...feedbackData
            };

            return { status: "success", feedback: newFeedback };
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }

    // READ - Get a single feedback by ID
    async getFeedback(id: string): Promise<Response> {
        try {
            const docRef = doc(this.fs, "feedbacks", id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                return {
                    status: "success",
                    feedback: {
                        id: docSnap.id,
                        ...docSnap.data()
                    } as Feedback
                };
            }
            return { status: "success" };
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }

    // READ - Get all feedbacks
    async getAllFeedbacks(): Promise<Responses> {
        try {
            const querySnapshot = await getDocs(this.collection);
            const feedbacks: Feedback[] = [];

            querySnapshot.forEach((doc) => {
                feedbacks.push({
                    id: doc.id,
                    ...doc.data()
                } as Feedback);
            });

            return { status: "success", feedbacks };
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }

    // READ - Get feedbacks by user ID
    async getFeedbacksByUser(userId: string): Promise<Responses> {
        try {
            const q = query(this.collection, where("userId", "==", userId));
            const querySnapshot = await getDocs(q);
            const feedbacks: Feedback[] = [];

            querySnapshot.forEach((doc) => {
                feedbacks.push({
                    id: doc.id,
                    ...doc.data()
                } as Feedback);
            });

            return { status: "success", feedbacks };
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }

    // READ - Get feedbacks by rating
    async getFeedbacksByRating(
        rating: 0 | 1 | 2 | 3 | 4 | 5
    ): Promise<Responses> {
        try {
            const q = query(this.collection, where("rating", "==", rating));
            const querySnapshot = await getDocs(q);
            const feedbacks: Feedback[] = [];

            querySnapshot.forEach((doc) => {
                feedbacks.push({
                    id: doc.id,
                    ...doc.data()
                } as Feedback);
            });

            return { status: "success", feedbacks };
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }

    // UPDATE - Update feedback
    async updateFeedback(
        id: string,
        updates: Partial<Omit<Feedback, "user" | "userId" | "createdAt">>
    ): Promise<Response> {
        try {
            const docRef = doc(this.fs, "feedbacks", id);
            await updateDoc(docRef, updates);
            return { status: "success" };
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }

    // DELETE - Delete feedback
    async deleteFeedback(id: string): Promise<Response> {
        try {
            const docRef = doc(this.fs, "feedbacks", id);
            await deleteDoc(docRef);
            return { status: "success" };
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }
}

// Example usage:
// async function example() {
//     const db = {} as Firestore; // Replace with your Firestore instance
//     const feedbackService = new FeedbackService(db);

//     try {
//         // Assuming you have a logged-in user with an ID
//         const userId = "user123";

//         // Create
//         const newFeedback = await feedbackService.createFeedback(userId, {
//             rating: 4,
//             message: "Great experience!"
//         });
//         console.log("Created:", newFeedback);

//         // Read single
//         const feedback = await feedbackService.getFeedback(newFeedback.id!);
//         console.log("Single feedback:", feedback);

//         // Read all
//         const allFeedbacks = await feedbackService.getAllFeedbacks();
//         console.log("All feedbacks:", allFeedbacks);

//         // Read by user
//         const userFeedbacks = await feedbackService.getFeedbacksByUser(userId);
//         console.log("User feedbacks:", userFeedbacks);

//         // Read by rating
//         const fourStarFeedbacks = await feedbackService.getFeedbacksByRating(4);
//         console.log("4-star feedbacks:", fourStarFeedbacks);

//         // Update
//         await feedbackService.updateFeedback(newFeedback.id!, {
//             rating: 5,
//             message: "Updated: Excellent experience!"
//         });
//         console.log("Updated feedback");

//         // Delete
//         await feedbackService.deleteFeedback(newFeedback.id!);
//         console.log("Deleted feedback");
//     } catch (error) {
//         console.error("Error in example:", error);
//     }
// }
